import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { observable } from 'mobx';
import { Button, FormGroup, Intent, TextArea } from '@blueprintjs/core';
import FormDrawer from 'components/FormDrawer';
import { FormSection, FieldError, Toggle } from 'components/FormBits';
import MultipleEmailSelector from 'components/MultipleEmailSelector';

// Emailing a work order, invoice, estimate or receipt, in a side panel.
// `object` is the store's send object (emailTo, emailSubject, emailMessage,
// emailCopyToSelf, emailAttached); the caller fills the subject and does the
// sending in `onSend` once To and Subject are filled in.
//
//   <SendEmailPanel isOpen what="work order" tone="field" object={object}
//     contacts={[{ email, name }]} busy={isLoading}
//     onEmailChange={…} onSend={…} onClose={…} />
@observer
class SendEmailPanel extends Component {
  @observable errors = {};

  onEmailChange = (emails) => {
    this.props.onEmailChange(emails);
    if (emails.length && this.errors.to) this.errors = { ...this.errors, to: null };
  };

  send = () => {
    const { object } = this.props;
    const errors = {};
    if (!object.emailTo || object.emailTo.length === 0) errors.to = 'Add at least one email address.';
    if (!object.emailSubject || !String(object.emailSubject).trim()) errors.subject = 'Enter a subject.';
    this.errors = errors;
    if (errors.to || errors.subject) return;
    this.props.onSend();
  };

  render() {
    const { isOpen, onClose, what, tone, object, contacts, busy } = this.props;
    const errors = this.errors;
    return (
      <FormDrawer
        isOpen={isOpen}
        title={`Send ${what}`}
        icon="envelope"
        tone={tone}
        size="min(760px, 100vw)"
        onClose={onClose}
        busy={busy}
        footer={
          <>
            <Button text="Cancel" onClick={onClose} disabled={busy} />
            <Button
              icon="send-message"
              text={`Send ${what}`}
              intent={Intent.PRIMARY}
              loading={busy}
              onClick={this.send}
            />
          </>
        }
      >
        <FormSection title="Email" hint={`Who gets the ${what} and what the email says.`}>
          <div className={`bp3-form-group${errors.to ? ' has-error' : ''}`}>
            <span className="fb-label" id="send-email-to-label">
              To<span className="fb-required" aria-hidden="true"> *</span>
            </span>
            <div className="send-email-to" role="group" aria-labelledby="send-email-to-label">
              <MultipleEmailSelector onEmailChange={this.onEmailChange} contacts={contacts} />
            </div>
            {errors.to
              ? <FieldError>{errors.to}</FieldError>
              : <p className="bp3-form-helper-text">Pick a contact or type an email address.</p>}
          </div>
          <FormGroup
            label={<span className="fb-label">Subject<span className="fb-required" aria-hidden="true"> *</span></span>}
            labelFor="send-email-subject"
            className={errors.subject ? 'has-error' : undefined}
          >
            <input
              id="send-email-subject"
              type="text"
              className="bp3-input bp3-fill"
              value={object.emailSubject || ''}
              onChange={(event) => {
                object.emailSubject = event.target.value;
                if (this.errors.subject) this.errors = { ...this.errors, subject: null };
              }}
            />
            <FieldError>{errors.subject}</FieldError>
          </FormGroup>
          <FormGroup label={<span className="fb-label">Message <span className="bp3-text-muted">(optional)</span></span>} labelFor="send-email-message">
            <TextArea
              id="send-email-message"
              className="bp3-fill"
              rows={4}
              placeholder={`e.g. Hi, here’s the ${what} for this week’s visit. Let us know if you have any questions.`}
              value={object.emailMessage || ''}
              onChange={(event) => { object.emailMessage = event.target.value; }}
            />
          </FormGroup>
        </FormSection>

        <FormSection title="Options" hint="What goes with the email.">
          <Toggle
            id="send-email-pdf"
            label="Attach as PDF"
            hint={`The ${what} goes along as a PDF file.`}
            checked={Boolean(object.emailAttached)}
            onChange={(checked) => { object.emailAttached = checked; }}
          />
          <Toggle
            id="send-email-copy"
            label="Send me a copy"
            hint="A copy goes to your own email address."
            checked={Boolean(object.emailCopyToSelf)}
            onChange={(checked) => { object.emailCopyToSelf = checked; }}
          />
        </FormSection>
      </FormDrawer>
    );
  }
}

SendEmailPanel.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  what: PropTypes.string.isRequired,
  tone: PropTypes.string,
  object: PropTypes.object.isRequired,
  contacts: PropTypes.array,
  busy: PropTypes.bool,
  onEmailChange: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired,
};

export default SendEmailPanel;
